import { DeleteOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  FormInstance,
  FormListFieldData,
  InputNumber,
  Row,
  Select
} from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

import {
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useInvestigationsServiceGetSupplyTemplateListEndpoint
} from 'src/api/queries'
import { requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  TestTypesInfiniteSelect,
  Text
} from 'src/components'
import { RECIPE_MEDICINE_FORM } from 'src/features/persons/utils'
import { TestConsumablesTables } from 'src/features/tests/components/TestConsumablesTables'
import {
  dayMonthYearHourMinutesFormatWithDot,
  disabledFutureDate
} from 'src/libs'
import { useLocales } from 'src/locales'

export const SCREENING_TESTS_FORM = 'items'

export type ScreeningTestRowValues = {
  investigationTypeId?: string
  testTypeId?: string
  resultOptionId?: string
  resultValueOptionId?: string
  resultValue?: number
  resultDate?: dayjs.Dayjs
  supplies?: ScreeningSupplyRow[]
}

export type ScreeningSupplyRow = {
  dispensedAmount?: number
  productFormId?: string
  internalInventoryItemId?: string
}

/** A fresh test row: the result date defaults to now. */
export const newScreeningTestRow = (): ScreeningTestRowValues => ({
  resultDate: dayjs()
})

type Props = {
  form: FormInstance
  field: FormListFieldData
  index: number
  /** Active rapid-test investigation types of the category and specimen. */
  investigationTypeOptions: { label?: string; value?: string }[]
  /** Fallback classifier for the result option when the test type defines none. */
  resultOptionClassifierId?: string
  /** Foreign persons are tested without stock consumption. */
  isForeign: boolean
  canRemove: boolean
  onRemove: () => void
}

/**
 * One test of a screening: its investigation type, test type, result and the
 * consumables of that test type. The consumables are edited in a form of their
 * own (the shared table works on a top-level list) and mirrored into the row's
 * `supplies` field of the screening form.
 */
export const ScreeningTestRow = ({
  form,
  field,
  index,
  investigationTypeOptions,
  resultOptionClassifierId,
  isForeign,
  canRemove,
  onRemove
}: Props) => {
  const { t, currentLang } = useLocales()
  const [suppliesForm] = Form.useForm()

  const rowPath = [SCREENING_TESTS_FORM, field.name]

  const investigationTypeId: string | undefined = Form.useWatch(
    [...rowPath, 'investigationTypeId'],
    form
  )
  const testTypeId: string | undefined = Form.useWatch(
    [...rowPath, 'testTypeId'],
    form
  )
  const resultOptionId: string | undefined = Form.useWatch(
    [...rowPath, 'resultOptionId'],
    form
  )

  // The investigation type carries its test types with their result options,
  // value options and unit, so the row only offers what the test type defines.
  const { data: investigationType, isFetching: isLoadingType } =
    useClassifierServiceGetInvestigationClassifierEndpoint(
      { id: String(investigationTypeId) },
      [investigationTypeId],
      { enabled: !!investigationTypeId }
    )

  const testType = investigationType?.testTypes?.find(
    (item) => item.id === testTypeId
  )
  const resultOptions = testType?.resultOptions ?? []
  // Test types configured without result options fall back to the full list.
  const fallbackToResultClassifier =
    !!investigationType && !isLoadingType && !resultOptions.length
  const valueOptions =
    resultOptions.find((option) => option.id === resultOptionId)
      ?.valueOptions ?? []
  const unitMeasure =
    testType?.resultUnitMeasure?.translation?.[currentLang?.value]

  const { data: supplyTemplate, isFetching: isLoadingTemplate } =
    useInvestigationsServiceGetSupplyTemplateListEndpoint(
      { typeId: String(testTypeId) },
      [testTypeId],
      { enabled: !!testTypeId && !isForeign }
    )

  // The consumables follow the chosen test type's supply template (e.g. the
  // rapid test kit); they stay editable.
  useEffect(() => {
    if (!testTypeId) {
      suppliesForm.setFieldsValue({ [RECIPE_MEDICINE_FORM]: [] })
      return
    }

    if (!supplyTemplate) return

    suppliesForm.setFieldsValue({
      [RECIPE_MEDICINE_FORM]:
        supplyTemplate.supplies?.map((supply) => ({
          name: supply.internationalName?.name,
          internalInventoryItemId: supply.product?.id,
          productFormName: supply.unit?.name,
          productFormId: supply.unit?.id,
          dispensedAmount: supply.quantity,
          stockQuantity: supply.currentQuantity
        })) ?? []
    })
  }, [supplyTemplate, testTypeId])

  const supplies = Form.useWatch(RECIPE_MEDICINE_FORM, suppliesForm)

  useEffect(() => {
    form.setFieldValue([...rowPath, 'supplies'], supplies ?? [])
  }, [supplies, field.name])

  const resetRowFields = (fields: (keyof ScreeningTestRowValues)[]) =>
    form.setFields(
      fields.map((name) => ({ name: [...rowPath, name], value: undefined }))
    )

  // The same test type of the same investigation type can be entered once.
  const duplicateRule = {
    validator: async (_: unknown, value?: string) => {
      if (!value) return

      const rows: ScreeningTestRowValues[] =
        form.getFieldValue(SCREENING_TESTS_FORM) ?? []

      const isDuplicate = rows.some(
        (row, rowIndex) =>
          rowIndex !== field.name &&
          row?.testTypeId === value &&
          row?.investigationTypeId === investigationTypeId
      )

      if (isDuplicate) throw new Error(t('screening.duplicate_test'))
    }
  }

  return (
    <Card
      size="small"
      title={
        <Text weight={600}>
          {t('screening.test_number', { number: index + 1 })}
        </Text>
      }
      extra={
        canRemove && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            aria-label={t('screening.remove_test')}
            title={t('screening.remove_test')}
          />
        )
      }
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            name={[field.name, 'investigationTypeId']}
            label={t('laboratory_classifiers.investigation_type')}
            rules={requiredRules}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={investigationTypeOptions}
              placeholder={t('laboratory_classifiers.investigation_type')}
              onChange={() =>
                resetRowFields([
                  'testTypeId',
                  'resultOptionId',
                  'resultValueOptionId',
                  'resultValue'
                ])
              }
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name={[field.name, 'testTypeId']}
            label={t('specimens.test_type')}
            rules={[{ required: true }, duplicateRule]}
          >
            <TestTypesInfiniteSelect
              key={investigationTypeId}
              disabled={!investigationTypeId}
              placeholder={t('specimens.test_type')}
              investigationTypeIds={
                investigationTypeId ? [investigationTypeId] : []
              }
              onChange={() =>
                resetRowFields([
                  'resultOptionId',
                  'resultValueOptionId',
                  'resultValue'
                ])
              }
            />
          </Form.Item>
        </Col>

        {testTypeId && (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                name={[field.name, 'resultOptionId']}
                label={t('tests_results.result')}
                rules={requiredRules}
              >
                {!fallbackToResultClassifier ? (
                  <Select
                    loading={isLoadingType}
                    options={resultOptions.map((option) => ({
                      value: option.id,
                      label: option.translation?.[currentLang?.value]
                    }))}
                    placeholder={t('tests_results.result')}
                    onChange={() =>
                      resetRowFields(['resultValueOptionId', 'resultValue'])
                    }
                  />
                ) : (
                  <ClassifierInfiniteSelect
                    className="full-width"
                    id={resultOptionClassifierId}
                    placeholder={t('tests_results.result')}
                  />
                )}
              </Form.Item>
            </Col>

            {!!valueOptions.length && (
              <Col xs={24} md={12}>
                <Form.Item
                  name={[field.name, 'resultValueOptionId']}
                  label={t('tests_results.result_value_option')}
                  rules={requiredRules}
                >
                  <Select
                    options={valueOptions.map((option) => ({
                      value: option.id,
                      label: option.translation?.[currentLang?.value]
                    }))}
                    placeholder={t('tests_results.result_value_option')}
                  />
                </Form.Item>
              </Col>
            )}

            {/* A numeric value only for test types measured in a unit. */}
            {!!unitMeasure && (
              <Col xs={24} md={12}>
                <Form.Item
                  name={[field.name, 'resultValue']}
                  label={`${t('specimens.result_value')} (${unitMeasure})`}
                >
                  <InputNumber className="full-width" />
                </Form.Item>
              </Col>
            )}

            <Col xs={24} md={12}>
              <Form.Item
                name={[field.name, 'resultDate']}
                label={t('tests.result_datetime')}
                rules={requiredRules}
              >
                <DatePicker
                  className="full-width"
                  showTime
                  showSecond={false}
                  disabledDate={disabledFutureDate}
                  format={dayMonthYearHourMinutesFormatWithDot}
                />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      <Form.Item name={[field.name, 'supplies']} hidden noStyle />

      {/* Always mounted so the consumables form stays connected. */}
      <Form form={suppliesForm} component={false}>
        {testTypeId && !isForeign && (
          <Form.List name={RECIPE_MEDICINE_FORM}>
            {(fields, { remove }) => (
              <TestConsumablesTables
                loading={isLoadingTemplate}
                fields={fields}
                remove={remove}
                form={suppliesForm}
              />
            )}
          </Form.List>
        )}
      </Form>
    </Card>
  )
}
